import { Component, OnInit } from '@angular/core';
import {
  ChargeSetupsService,
  ChargeInformationDto,
  ChargeInformationService,
  ChargeSetupDto,
  CreateChargeSetupCommand,
  ChargeType,
  LoadLevel,
  ObservationTimeSeriesPeriod,
  UpdateChargeSetupCommand
} from '@app/api/dmc';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormApiService } from '@app/core/services/form-api.service';
import { InputDateDirective } from '@shared/components/input/date/input-date.directive';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { InputNumberDirective } from '@shared/components/input/number/input-number.directive';
import { LabelDirective } from '@shared/components/input/label/label.directive';
import { TranslatePipe } from '@ngx-translate/core';
import { dateGreaterThanEqualValidator } from '@shared/validators/date-greater-than-equal.validator';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

type ChargeSetupForm = {
  validFrom: FormControl<string | null>;
  loadLevelNone: FormControl<number | null>;
  loadLevelLow: FormControl<number | null>;
  loadLevelHigh: FormControl<number | null>;
  loadLevelPeak: FormControl<number | null>;
};

@Component({
  selector: 'app-chargeinformation-chargesetup-openform',
  imports: [
    ReactiveFormsModule,
    InputDateDirective,
    InputGroupComponent,
    InputNumberDirective,
    LabelDirective,
    TranslatePipe
  ],
  templateUrl: './chargeinformation-chargesetup-openform.component.html',
  styleUrl: './chargeinformation-chargesetup-openform.component.css'
})
export class ChargeinformationChargesetupOpenformComponent implements OnInit, HasUnsavedChanges {
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public chargeInformationId?: string;
  public chargeInformation?: ChargeInformationDto;
  public chargeSetupDto?: ChargeSetupDto;
  public readonly firstValidDate = new Date(new Date().getFullYear(), new Date().getMonth() + 2, 1);
  public readonly firstValidDateString =
    `${this.firstValidDate.getFullYear()}-${String(this.firstValidDate.getMonth() + 1).padStart(2, '0')}-${String(this.firstValidDate.getDate()).padStart(2, '0')}`;
  public form = new FormGroup<ChargeSetupForm>({
    validFrom: new FormControl<string | null>(null, [Validators.required, dateGreaterThanEqualValidator(this.firstValidDate)]),
    loadLevelNone: new FormControl<number | null>(null),
    loadLevelLow: new FormControl<number | null>(null),
    loadLevelHigh: new FormControl<number | null>(null),
    loadLevelPeak: new FormControl<number | null>(null),
  });

  constructor(
    private readonly chargeInformationService: ChargeInformationService,
    private readonly chargeSetupsService: ChargeSetupsService,
    private readonly formApiService: FormApiService,
  ) {
  }

  public ngOnInit(): void {
    if (this.chargeInformation?.id) {
      this.initializeForm();
      return;
    }

    if (this.chargeInformationId) {
      this.chargeInformationService.chargeInformationGetByIdGet(this.chargeInformationId).subscribe((chargeInformation) => {
        this.chargeInformation = chargeInformation;
        this.initializeForm();
      });
    }
  }

  public get usesDifferentiatedPrices(): boolean {
    return this.chargeInformation?.chargeType === ChargeType.Tariff &&
      this.chargeInformation.observationPeriod === ObservationTimeSeriesPeriod.Hour;
  }

  private get resolvedChargeInformationId(): string | undefined {
    return this.chargeInformationId ?? this.chargeInformation?.id;
  }

  public createChargeSetup(): void {
    if (this.form.valid && this.resolvedChargeInformationId) {
      this.formApiService.submit(
        this.form,
        () => this.chargeSetupsService.chargeInformationsChargeInformationIdChargeSetupsPost(
          this.resolvedChargeInformationId!,
          this.createCreateChargeSetupCommand()),
        {
          successMessage: 'chargeSetup.chargeSetupCreated',
          closeModal: true,
        }
      );
      return;
    }

    this.form.markAllAsTouched();
  }

  public saveChanges(): void {
    if (!this.form.valid || !this.chargeSetupDto || !this.resolvedChargeInformationId) {
      this.form.markAllAsTouched();
      return;
    }

    this.formApiService.submit(
      this.form,
      () => this.chargeSetupsService.chargeInformationsChargeInformationIdChargeSetupsIdPut(
        this.resolvedChargeInformationId!,
        this.chargeSetupDto!.id!,
        this.createUpdateChargeSetupCommand()),
      {
        successMessage: 'chargeSetup.chargeSetupUpdated',
        closeModal: true,
      }
    );
  }

  private initializeForm(): void {
    this.updatePriceValidators();

    if (!this.chargeSetupDto) {
      return;
    }

    const validFrom = this.chargeSetupDto.validFrom
      ? new Date(this.chargeSetupDto.validFrom).toISOString().split('T')[0]
      : null;

    this.form.patchValue({
      validFrom,
      loadLevelNone: this.chargeSetupDto.loadLevelNone ?? null,
      loadLevelLow: this.chargeSetupDto.loadLevelLow ?? null,
      loadLevelHigh: this.chargeSetupDto.loadLevelHigh ?? null,
      loadLevelPeak: this.chargeSetupDto.loadLevelPeak ?? null,
    });
  }

  private updatePriceValidators(): void {
    this.setRequiredValidator(this.form.controls.loadLevelNone, !this.usesDifferentiatedPrices);
    this.setRequiredValidator(this.form.controls.loadLevelLow, this.usesDifferentiatedPrices);
    this.setRequiredValidator(this.form.controls.loadLevelHigh, this.usesDifferentiatedPrices);
    this.setRequiredValidator(this.form.controls.loadLevelPeak, this.usesDifferentiatedPrices);
  }

  private createCreateChargeSetupCommand(): CreateChargeSetupCommand {
    return {
      validFrom: this.form.controls.validFrom.value!,
      loadLevelPrices: this.createLoadLevelPrices(),
    };
  }

  private createUpdateChargeSetupCommand(): UpdateChargeSetupCommand {
    return {
      validFrom: this.form.controls.validFrom.value!,
      loadLevelPrices: this.createLoadLevelPrices(),
    };
  }

  private createLoadLevelPrices(): Array<{ price: number; loadLevel: LoadLevel }> {
    if (this.usesDifferentiatedPrices) {
      return [
        { price: this.form.controls.loadLevelLow.value ?? 0, loadLevel: LoadLevel.Low },
        { price: this.form.controls.loadLevelHigh.value ?? 0, loadLevel: LoadLevel.High },
        { price: this.form.controls.loadLevelPeak.value ?? 0, loadLevel: LoadLevel.Peak },
      ];
    }

    return [
      { price: this.form.controls.loadLevelNone.value ?? 0, loadLevel: LoadLevel.None },
    ];
  }

  private setRequiredValidator(control: FormControl<number | null>, required: boolean): void {
    if (required) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }

    control.updateValueAndValidity({ emitEvent: false });
  }
}
